import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { pluralReadableTypeName, readableTypeName } from '@iotv/core-services';
import { classDerivedDeviceEnabledTypes, DeviceClassName, GatewayClassName, getParentEdgeTypeId, } from '@iotv/datamodel';
import { AdjacentType, ComparisonType, getObjectHash, } from '@iotv/iotv-v3-types';
import { ButtonGroup, CircularProgress, Stack } from '@mui/material';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { v4 } from 'uuid';
import { shortDefs } from '../../../ArbitrationObjects/ListControlArbitrator/shortDefs';
import { AppMainViewContext } from '../../../ContextProviders/AppContextProvider';
import Confirmation from '../../../Popup/Confirmation';
import useQuestion from '../../../Popup/useQuestion';
import { ConsumerDao } from '../../../data/ConsumerDAO';
import { isAdminRole } from '../../../helpers/access';
import { harvestSupportedTypeFromNodeFilters } from '../../../helpers/queryHelpers';
import { ComponentMode, Severity, ShowDefinedEntriesOnlyOpts, } from '../../../types';
import { DataGridRadioColumn } from '../../../util/DataGrid/DataGridHelpers';
import { getViewDefinitionForNewObject } from '../../../v2/components/ExceptionTypesHandling/viewDefinitions';
import { MoveBetweenSupportedParentEdges } from '../../../v2/components/dialogs/moveItems/MoveBetweenSupportedParentEdges';
import { getAllBeneathCustomerQuery } from '../../../v2/components/dialogs/moveItems/queries';
import { ObjectDetailCard } from '../../../v2/components/generic/ObjectView/ObjectDetailCard';
import { defaulColumnVisibilityModel } from '../../../v2/components/generic/ObjectsDataTable/fns';
import { viewDefinitionToGridColumnMap } from '../../../v2/components/generic/transformFns';
import { DialogWrapper } from '../../common/DiaglogWrapper';
import { StripedDataGrid } from '../DataGrid';
import { QueryObjectSearch } from './QueryObjectSearch';
import { getAddButtonFn, getMoveButtonFn, getRemoveButtonFn, getSearchButtonFn, getUnlinkButtonFn } from './buttons';
import { handleDeleteSelectedObjects } from './fns/handleDeleteSelectedItems';
import { handleLinkSelectedObjects } from './fns/handleLinkSelectedItems';
import { mutateListerlizerTransactionFns } from './fns/mutateTransactionFns';
import { getBranchingQueryDefFromAdjacentFilter } from './queries';
const debug = process.env.REACT_APP_DEBUG && false;
const LoadingOverlay = (props) => (_jsx(Stack, { ...props, alignSelf: "center", width: "100%", height: "100%", justifyContent: "center", alignItems: "center", children: _jsx(CircularProgress, { disableShrink: true }) }));
/** @deprecated in favour of {@link DataGridRadioColumn | `DataGridRadioColumn`} */
export const radioCheckboxColumnDef = DataGridRadioColumn;
/** Parses an adjacentFilters filter groups to find a supported type for groups
 * WARN: pretty basic, might need extending if you find the Listerlizer link items dialog misbehaving
 *
 * @param adjacentFilter - The adjacent filter to check
 * @returns the supportedType or null if none could be found
 * */
export const harvestSupportedTypeFromAdjacentFilter = (a) => {
    if (a.objectType !== 'Group')
        return null;
    if (!a.filterConfig)
        return null;
    for (const fg of a.filterConfig) {
        for (const filter of fg.filters) {
            if (filter.key === 'supportedType' && filter.predicate === ComparisonType.EQUALS)
                return filter.value ?? null;
        }
    }
    return null;
};
/** Constructs a restricted query based on the provided search root object and the adjacent filter
 * @param searchRootObjects - Objects to use as context. The first object in this list is treated as the canonical type, and all search root objects must be the same type. If you don't want this condition, you can construct your own link query.
 * @param adjacentFilter - The adjacent filter passed to the listerlizer will be used to obtain the desired type, then a itemTypeToGroupNestedDescedingQuery is found using it. If the type is group we try to infer a supportedType from constraints.
 * */
const constructLinkableItemsBranchingQuery = (searchRootObjects, adjacentFilter) => {
    // Mkae sure search root object can be used in a branching query def
    const latestType = searchRootObjects[0].type;
    debug && console.log(`constructLinkableItemsBranchingQuery ${adjacentFilter.objectType}`, { searchRootObjects, adjacentFilter });
    for (const searchRootObject of searchRootObjects) {
        if (searchRootObject.type !== latestType) {
            console.error(`The automatic listerlizer link items query requires your search root objects (searchRootObjects ?? [contextObject ?? mostProximateCustomer]) to be all the same type. Expected ${latestType}, found ${searchRootObject.type}`);
            throw new Error('There is an error in the setup to display this list. Please check developer console for more information.');
        }
        if (!['Customer', 'Partner'].includes(searchRootObject.type)) {
            console.error(`The automatic listerlizer query requires your search root objects (searchRootObjects ?? [contextObject ?? mostProximateCustomer]) to be Customers or Partners. Found ${searchRootObject.type}`);
            throw new Error('Insufficient information to display list. Please check developer console for more information.');
        }
    }
    let soughtType = adjacentFilter.objectType;
    // Make sure if we're looking for 'Group' that there is a supported type
    if (adjacentFilter.objectType === 'Group') {
        const inferredSupportedType = adjacentFilter.groupSupportedType ?? harvestSupportedTypeFromAdjacentFilter(adjacentFilter);
        if (inferredSupportedType) {
            debug && console.log('Listerlizer', 'using inferred supported type while looking for groups in get linkable items', inferredSupportedType);
            soughtType = `${inferredSupportedType}`; // This might be anumber so we wrap it in string.
            //} else if (adjacentFilter.otherObjectType) {
            //  soughtType = `${adjacentFilter.otherObjectType}` // This might be anumber so we wrap it in string.
        }
        else {
            console.error("Listerlizer looking for groups has no provided linkable items query and no supported type could be inferred from the adjacent filter config. Did you add a {key: 'supportedType', predicate: ComparisonType.EQUALS, value: {supportedType}} to your adjacentFilter filterConfigs? If you don't want to do that, please provide a linkableItemsBrancingQueryDefinition.", adjacentFilter);
            throw new Error('Insufficient information to display list. Please check developer console for more information.');
        }
    }
    return getAllBeneathCustomerQuery([soughtType]);
    //return getType({
    //  groupDefiningObjectTypeId: latestType as 'Customer' | 'Partner',
    //  supportedType: soughtType,
    //  includeSupportedTypeInstances: true, // Needs to be true
    //})
};
export const Listerlizer = (props) => {
    const { adjacentFilter, viewObject, contextObject, mostProximateCustomer, searchRootObjects, allowTransactionFunctionMutations = true, viewDefinition, newObjectViewDefinition: newObjectViewDefinitionIn, autoQuery = true, transactionFunctions, userFunctions, selectorConfigParams: selectorConfigParamsIn, initialSort = [{ field: 'name', sort: 'asc' }], listItemsQueryOverride, linkableItemsBranchingQueryDefinition, linkableItemsResultFilter, linkableItemsViewDefinition, applicationData, AddComponentOverride = ObjectDetailCard, } = props;
    const { matchedPrimary } = viewObject;
    const filteredViewDef = Object.fromEntries(Object.entries(viewDefinition).filter(([key, def]) => def.hideFromList !== true));
    /** STATE */
    const [{ contextCustomer, contextUser, roleArbitrationInstance, isSmallView }] = useContext(AppMainViewContext);
    // Stuff we DISPLAY IN THE LIST
    const [loading, setLoading] = useState(false);
    const listItems = matchedPrimary && applicationData
        ? applicationData
            .applyBranchingQuery([matchedPrimary], listItemsQueryOverride ?? getBranchingQueryDefFromAdjacentFilter(adjacentFilter))
            .getNodesOfType(adjacentFilter.objectType)
            .filter((vob) => vob.pk !== matchedPrimary.pk && vob.sk !== matchedPrimary.sk)
        : [];
    debug && console.log(`Listerlizer from ${matchedPrimary?.type} to ${adjacentFilter.objectType} (${adjacentFilter.adjacentType}), list items`, listItems);
    /** This will be the main app context customer - be carefull if the transaction needs one from an adjacent in the tree */
    //const mode = isViewOnly(roleArbitrationInstance?.groupName) ? ComponentMode.VIEW : (initMode ?? ComponentMode.VIEW)
    const [showSearch, setShowSearch] = useState(false);
    const [showMoveDialog, setShowMoveDialog] = useState(false);
    const [newObject, setNewObject] = useState(undefined);
    const [deleteConfirmation, setDeleteConfirmation] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [resolve, setResolve] = useState(() => () => { });
    const [confirmation, setConfirmation] = useState({ title: 'Confirm?', question: 'Are you sure?' });
    const { Dialogue, setOpen } = useQuestion({ ...confirmation, result: (r) => resolve(r) });
    const getConfirmation = (props) => {
        setConfirmation(props);
        setOpen(true);
        return new Promise((resolve) => setResolve(() => resolve));
    };
    /** hook to bake in userFunctions.addObjectsToState to save action for custom behavior eg where consuming component does not get objects from AppState, this adds ObjectsToState when processRowUpdate is called  */
    // TODO: does this have side effects?
    //const { onProcessRowUpdateError, processRowUpdate, snackBarState } = useDatastorePersistForDataGrid(userFunctions)
    /** mutates the SAVE functions on new object dialog */
    const mutatedTransactionFunctions = useMemo(() => {
        const mutateFns = allowTransactionFunctionMutations;
        return mutateFns
            ? mutateListerlizerTransactionFns({
                transactionFunctions,
                userFunctions,
                resolvedContextObject: matchedPrimary ?? contextObject,
                newObject,
                adjacentFilter,
                contextUser,
                contextCustomer,
                mostProximateCustomer,
                setNewObject,
                getConfirmation,
            })
            : transactionFunctions;
    }, [
        allowTransactionFunctionMutations,
        userFunctions,
        contextUser,
        newObject,
        contextCustomer,
        mostProximateCustomer,
        adjacentFilter,
        matchedPrimary,
        contextObject,
        transactionFunctions,
    ]);
    /** OBJECT SELECTION */
    const [selectedObjects, setSelectedObjects] = useState([]);
    const updateItemSelection = (rowIds, details) => {
        // TODO: is this weird when you move, is stuff auto deselected, or deselected at all???
        debug && console.log('Listerlizer', 'Updating Item Selection');
        const selectedIds = 'ids' in rowIds ? Array.from(rowIds.ids) : rowIds;
        setSelectedObjects(selectedIds.map((id) => details.api.getRow(id)).filter((x) => Boolean(x)));
    };
    /** CONTROLS */
    /** default new object viewDefinition if none is provided — as would normally be the case */
    const newObjectViewDefinition = newObjectViewDefinitionIn ?? getViewDefinitionForNewObject(newObject);
    debug && console.log('Listerlizer', 'newObjectViewDefinition', newObjectViewDefinition);
    const uniqueDialogueKeyPrefix = `${matchedPrimary?.sk}`;
    const allowedControls = selectorConfigParamsIn ??
        (matchedPrimary && roleArbitrationInstance
            ? { controls: roleArbitrationInstance.adjacentQueryToControlActions({ contextObject: matchedPrimary, adjacentFilter }) }
            : { controls: shortDefs.noAction });
    const mode = allowedControls.controls.edit === true ? ComponentMode.EDIT : ComponentMode.VIEW;
    debug && console.log('Listerlizer', 'allowed controls', allowedControls);
    const getAddButton = getAddButtonFn({
        adjacentFilter,
        matchedPrimary,
        setNewObject,
        viewDefinition: newObjectViewDefinitionIn,
        disabled: newObject !== undefined,
    });
    const getSearchButton = getSearchButtonFn({ setShowSearch, showSearch });
    const getUnlinkButton = getUnlinkButtonFn({ adjacentFilter, matchedPrimary, selectedObjects, transactionFunctions: mutatedTransactionFunctions });
    const getMoveButton = getMoveButtonFn({ setShowMoveDialog, showMoveDialog, selectedObjects });
    /** Needed for remove dialog */
    const location = useLocation();
    const getRemoveButton = getRemoveButtonFn({
        selectedObjects,
        setConfirmation: setDeleteConfirmation,
        disabled: deleteConfirmation || deleting,
        loading: deleting,
    });
    const controls = {
        getAddButton,
        getLinkButton: getSearchButton,
        getUnlinkButton,
        getMoveButton,
        getRemoveButton,
    };
    /** LINKING */
    const linkSearchRootObjects = searchRootObjects ?? (contextObject ? [contextObject] : undefined) ?? (mostProximateCustomer ? [mostProximateCustomer] : undefined);
    /** Query to get linkable items. Falls back to query nested descending querying from searchRootObject (or context object (or MPC)) to displayed type */
    const getLinkableItemsBranchingQueryDefinition = linkableItemsBranchingQueryDefinition ??
        (linkSearchRootObjects && allowedControls.controls.find ? constructLinkableItemsBranchingQuery(linkSearchRootObjects, adjacentFilter) : undefined);
    // Falls back to very broad definition
    const resultFilter = linkableItemsResultFilter ?? {
        objectTypeId: adjacentFilter.objectType,
        nodeFilters: adjacentFilter.filterConfig,
        adjacencyType: adjacentFilter.adjacentType,
        // TODO: edge filters?
    };
    /** Applied to edges created between newly linked items  WARN: NOT added items, only linked! */
    const newLinkEdgeAttributes = props.newLinkEdgeAttributes;
    /** in this case refs + dataGridHash/ onSelectedUpdate are used as a proxy for state and dispatch state , in future it is planned to use a constructed context provided to do this job */
    /** FETCHING ITEMS FOR LIST */
    /** Main effect that loads things into app state, if {@link autoQuery} is enabled */
    useEffect(() => {
        if (!matchedPrimary || !autoQuery)
            return;
        listItems.length === 0 && setLoading(true);
        ConsumerDao.getGraphQueryRequestV2({
            query: listItemsQueryOverride ?? getBranchingQueryDefFromAdjacentFilter(adjacentFilter),
            contextObjects: [matchedPrimary],
        }).then(({ err, data }) => {
            setLoading(false);
            debug && console.log(`Listerlizer queried from ${matchedPrimary.type} to ${adjacentFilter.objectType} (${adjacentFilter.adjacentType})`, err, data);
            if (err || !data) {
                userFunctions.setUserMessage({
                    id: v4(),
                    type: 'MessageBar',
                    severity: Severity.error,
                    label: 'Error getting items',
                    content: `There was an error retrieving items for ${matchedPrimary.name}, please check the developer console for more information.`,
                });
                console.error(err ? err.message : 'No data on graph query');
            }
            if (data)
                userFunctions.addObjectsToState(data.getContainedNodesAndEdges());
        });
    }, [matchedPrimary?.sk, autoQuery, listItems.length, getObjectHash(listItemsQueryOverride ?? adjacentFilter)]);
    /**
     * The linkable items dialog. Uses provided query to get items if provided, otherwise does a query from most proximate customer down.
     *
     * INFO: default behaviour is MOST PROXIMATE customer, not context customer, though context customer is more in line with LSC, most proximate customer is safer. You can provide context customer as the search root object if you want
     */
    const SearchLinkableItemsDialog = linkSearchRootObjects && getLinkableItemsBranchingQueryDefinition && matchedPrimary ? (_jsx(QueryObjectSearch, { branchingQueryDefinition: getLinkableItemsBranchingQueryDefinition,
        matchedPrimary,
        resultFilter,
        searchRootObjects: linkSearchRootObjects, // WARN: may give trouble
        setClosed: () => setShowSearch(false),
        linkableItemsViewDefinition: linkableItemsViewDefinition ?? filteredViewDef,
        visibleGroupsOnly: [...classDerivedDeviceEnabledTypes, GatewayClassName, DeviceClassName].includes(harvestSupportedTypeFromNodeFilters(resultFilter.nodeFilters) ?? '') && !isAdminRole(roleArbitrationInstance.groupName),
        handleLink: (linkItems) => {
            handleLinkSelectedObjects({
                createEdgeAdjacentFilter: {
                    ...adjacentFilter,
                    edgeFilter: {
                        // Got construct the edge type id here as it might not be present. If it is present it'll be overwritten below
                        edgeTypeId: (adjacentFilter.adjacentType === AdjacentType.CHILD
                            ? getParentEdgeTypeId(adjacentFilter.objectType, matchedPrimary.type)
                            : getParentEdgeTypeId(matchedPrimary.type, adjacentFilter.objectType)) ?? 'never',
                        ...adjacentFilter.edgeFilter,
                    },
                },
                matchedPrimary,
                selectedObjects: linkItems,
                setShowSearch,
                transactionFunctions,
                edgeAttributes: newLinkEdgeAttributes,
            });
        } })) : (_jsx(_Fragment, {}));
    const suppressAddControl = roleArbitrationInstance?.groupName !== 'HyperUser' && adjacentFilter.adjacentType === AdjacentType.PARENT;
    const suppressMoveControl = roleArbitrationInstance?.groupName !== 'HyperUser' && adjacentFilter.adjacentType === AdjacentType.PARENT;
    const suppressRemoveControl = roleArbitrationInstance?.groupName !== 'HyperUser' && adjacentFilter.adjacentType === AdjacentType.PARENT;
    const resolvedControls = {
        add: allowedControls.controls.add && !suppressAddControl,
        find: allowedControls.controls.find,
        move: allowedControls.controls.move && !suppressMoveControl,
        unlink: allowedControls.controls.unlink,
        delete: allowedControls.controls.delete && !suppressRemoveControl,
        /* Edit control for adding new items */
        edit: allowedControls.controls.edit && !suppressAddControl,
    };
    const allowSelection = Object.values(resolvedControls).filter((v) => v).length > 0;
    /** DATA GRID */
    const columnMap = { ...viewDefinitionToGridColumnMap(filteredViewDef, true) };
    const columns = useMemo(() => allowSelection
        ? [DataGridRadioColumn, ...Object.entries(columnMap).map(([k, v]) => ({ field: k, ...v }))]
        : [...Object.entries(columnMap).map(([k, v]) => ({ field: k, ...v }))], [columnMap, selectedObjects, allowSelection]);
    const dataGridProps = {
        columns,
        columnVisibilityModel: defaulColumnVisibilityModel,
        loading,
        rows: listItems,
        getRowClassName: (params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'),
        density: 'compact',
        pageSizeOptions: [10],
        hideFooter: listItems.length === 0 || (!allowSelection && listItems.length <= 10),
        hideFooterPagination: listItems.length <= 10,
        checkboxSelection: allowSelection,
        disableRowSelectionExcludeModel: true,
        onRowSelectionModelChange: updateItemSelection,
        initialState: { sorting: { sortModel: initialSort }, pagination: { paginationModel: { pageSize: 10 } } },
        slots: { loadingOverlay: LoadingOverlay },
    };
    return (_jsxs(Stack, { gap: 1, children: [allowSelection && mode === ComponentMode.EDIT && (_jsxs(ButtonGroup, { children: [resolvedControls.add && controls.getAddButton(), resolvedControls.find && controls.getLinkButton(), resolvedControls.move && controls.getMoveButton(), resolvedControls.unlink && controls.getUnlinkButton(), resolvedControls.delete && controls.getRemoveButton()] })), _jsx(StripedDataGrid, { style: { fontSize: 'inherit' }, ...dataGridProps }), matchedPrimary && (_jsx(DialogWrapper, { header: `Link to ${contextObject?.name ?? (contextObject?.type ? readableTypeName(contextObject.type) : 'Entity')}`,
                open: showSearch,
                setClosed: () => setShowSearch(false),
                children: SearchLinkableItemsDialog }, `${uniqueDialogueKeyPrefix}_linkDialog`)), matchedPrimary && selectedObjects.length > 0 && (_jsx(DialogWrapper, { header: debug
                    ? `Move ${selectedObjects.length ? pluralReadableTypeName(adjacentFilter.objectType) : readableTypeName(adjacentFilter.objectType)} from ${matchedPrimary.name} - contextObject: ${matchedPrimary?.name}, find: ${adjacentFilter.objectType}, supportedType? ${JSON.stringify(adjacentFilter)}`
                    : `Move ${selectedObjects.length ? pluralReadableTypeName(adjacentFilter.objectType) : readableTypeName(adjacentFilter.objectType)} from ${matchedPrimary.name}`,
                open: showMoveDialog,
                setClosed: (something) => setShowMoveDialog(false), children: _jsx(MoveBetweenSupportedParentEdges, { sourceContainer: matchedPrimary, objectsToMove: selectedObjects, onlyVisibleGroups: !isAdminRole(roleArbitrationInstance?.groupName) }) }, `${uniqueDialogueKeyPrefix}_moveDialog`)), _jsx(DialogWrapper, { header: `Add ${newObject?.type ? readableTypeName(newObject.type) : 'Entity'} to ${matchedPrimary?.name}`,
                open: newObject !== undefined,
                setClosed: (something) => {
                    setNewObject(undefined);
                },
                children: newObject ? (_jsx(AddComponentOverride, { viewDefinition: newObjectViewDefinition,
                    allowedActions: { ...resolvedControls, delete: false },
                    transactionFunctions: mutatedTransactionFunctions,
                    matchedPrimary: newObject,
                    contextObject: contextObject,
                    mostProximateCustomer,
                    subContextCustomer: mostProximateCustomer,
                    userFunctions: userFunctions,
                    editMode: ComponentMode.EDIT,
                    definedEntriesOnlyOpt: ShowDefinedEntriesOnlyOpts.Yes,
                    actions: [],
                    setClosed: (something) => {
                        setNewObject(undefined);
                    },
                    contextUser,
                    isSmallView })) : (_jsx(_Fragment, {})) }, `${uniqueDialogueKeyPrefix}_newObjectDialog`), _jsx(Confirmation, { name: `Remove ${adjacentFilter?.objectType ? readableTypeName(adjacentFilter.objectType) : 'Entity'}`,
                message: `Are you sure you want to remove ${selectedObjects.length} ${selectedObjects.length !== 1 ? pluralReadableTypeName(adjacentFilter.objectType) : readableTypeName(adjacentFilter.objectType)}?`,
                state: deleteConfirmation,
                changeState: setDeleteConfirmation,
                action: 'Remove',
                actionFunction: () => {
                    setDeleting(true);
                    handleDeleteSelectedObjects({ matchedPrimary, selectedObjects, transactionFunctions, userFunctions, location }).then(() => setDeleting(false));
                } }), Dialogue] }));
};
